import React, {useMemo, useState} from "react";
import {makeStyles, Slider, Tooltip} from "@material-ui/core";

const useStyles = makeStyles(() => ({
  container: {
    overflowX: "auto",
  },
  title: {
    fontSize: "14px",
    fontWeight: 600,
    marginBottom: "8px",
    color: "#353945",
  },
  table: {
    borderCollapse: "collapse",
    fontSize: "12px",
    "& th, & td": {
      padding: "4px 8px",
      textAlign: "center",
      border: "1px solid #e0e0e0",
      minWidth: "36px",
    },
    "& th": {
      backgroundColor: "#f5f5f5",
      fontWeight: 600,
      color: "#555",
    },
  },
  cell: {
    cursor: "default",
    transition: "opacity 0.15s",
    "&:hover": {
      opacity: 0.8,
    },
  },
}));

interface HeatMatrixCell {
  row: number;
  col: number;
  count: number;
}

interface AxisEntry {
  value: number;
  label: string;
}

interface HeatMatrixProps {
  cells: HeatMatrixCell[];
  rows: AxisEntry[];
  cols: AxisEntry[];
  title?: string;
}

const interpolateColor = (ratio: number): string => {
  const clamped = Math.max(0, Math.min(1, ratio));
  const r = Math.round(255 - clamped * 200);
  const g = Math.round(255 - clamped * 80);
  const b = Math.round(255 - clamped * 200);
  return `rgb(${r}, ${g}, ${b})`;
};

const gradientString = `linear-gradient(to right, ${interpolateColor(0)}, ${interpolateColor(1)})`;

const MIN_MAX = 1;

export const HeatMatrix = ({ cells, rows, cols, title }: HeatMatrixProps) => {
  const classes = useStyles();

  const dataMaxCount = useMemo(() => {
    let max = 0;
    cells.forEach((cell) => {
      if (cell.count > max) max = cell.count;
    });
    return Math.max(max, MIN_MAX);
  }, [cells]);

  const [userMax, setUserMax] = useState<number | null>(null);
  const effectiveMax = userMax ?? dataMaxCount;

  const grid = useMemo(() => {
    const g = new Map<string, number>();
    cells.forEach((cell) => {
      g.set(`${cell.row}-${cell.col}`, cell.count);
    });
    return g;
  }, [cells]);

  if (cells.length === 0) return null;

  return (
    <div className={classes.container}>
      {title && <div className={classes.title}>{title}</div>}
      <table className={classes.table}>
        <thead>
          <tr>
            <th />
            {cols.map((col) => (
              <th key={col.value}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.value}>
              <th>{row.label}</th>
              {cols.map((col) => {
                const count = grid.get(`${row.value}-${col.value}`) ?? 0;
                const ratio = effectiveMax > 0 ? Math.min(count / effectiveMax, 1) : 0;
                return (
                  <Tooltip key={col.value} title={`${count}`} arrow placement="top">
                    <td
                      className={classes.cell}
                      style={{
                        backgroundColor: count > 0 ? interpolateColor(ratio) : "#fafafa",
                        color: ratio > 0.6 ? "#fff" : "#333",
                      }}
                    >
                      {count > 0 ? count : ""}
                    </td>
                  </Tooltip>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: "8px", fontSize: "10px" }}>
        <div style={{
          height: "8px",
          borderRadius: "2px",
          background: gradientString,
        }} />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2px", color: "#666" }}>
          <span>0</span>
          <span>{effectiveMax}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px", paddingRight: "8px" }}>
          <span style={{ color: "#666", whiteSpace: "nowrap", minWidth: "100px" }}>
            Max: <strong>{effectiveMax}</strong>
          </span>
          <Slider
            value={effectiveMax}
            onChange={(_, value) => setUserMax(value as number)}
            min={MIN_MAX}
            max={dataMaxCount}
            step={Math.max(1, Math.round(dataMaxCount / 100))}
            style={{ flex: 1 }}
          />
        </div>
      </div>
    </div>
  );
};
